import React, { useState, useEffect, useCallback } from "react";
import Tilt3D from "./Tilt3D";
import { BLUE, BLUE_DEEP, BLUE_LIGHT, BORDER, TEXT, TEXT2, TEXT3, WHITE } from "../../shared";

/**
 * SocialPreviewCard
 * -----------------
 * Renders a platform-accurate mockup of a generated social post. Tilts on
 * hover, clicks to expand into a full-size preview with edit options.
 *
 * Props:
 *   - platform: "Instagram" | "Facebook" | "TikTok" | "LinkedIn"
 *   - postType: "Feed" | "Reel" | "Story" | "Post"      (default depends on platform)
 *   - brand:    { name, handle?, avatarUrl?, verified? }
 *   - asset:    { url, type?: "image" | "video", alt? }
 *   - caption:  string
 *   - hashtags: string[]
 *   - onEdit, onRegenerate, onUseIt
 */
export default function SocialPreviewCard({
  platform = "Instagram",
  postType,
  brand = {},
  asset = {},
  caption = "",
  hashtags = [],
  onEdit,
  onRegenerate,
  onUseIt,
}) {
  const [expanded, setExpanded] = useState(false);
  const t = (postType || defaultPostTypeFor(platform)).toLowerCase();
  const platformKey = platform.toLowerCase();

  const close = useCallback(() => setExpanded(false), []);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, close]);

  return (
    <>
      <Tilt3D
        onClick={() => setExpanded(true)}
        role="button"
        ariaLabel={`Open ${platform} ${t} preview`}
        style={{
          width: "100%",
          maxWidth: 360,
          background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(244,250,255,0.86) 100%)",
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 22px 50px -22px rgba(11,107,211,0.30), 0 10px 28px -14px rgba(14,165,233,0.18)",
          overflow: "hidden",
          padding: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <PlatformBadge platform={platformKey} postType={t} />
          <div style={{ fontSize: 10, color: TEXT3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Tap to expand</div>
        </div>

        <div style={{ borderRadius: 14, overflow: "hidden", boxShadow: "0 6px 18px -8px rgba(10,27,58,0.25)" }}>
          <PlatformMockup
            platform={platformKey}
            postType={t}
            brand={brand}
            asset={asset}
            caption={caption}
            hashtags={hashtags}
            compact
          />
        </div>
      </Tilt3D>

      {expanded ? (
        <ExpandedSocial
          platform={platformKey}
          postType={t}
          brand={brand}
          asset={asset}
          caption={caption}
          hashtags={hashtags}
          onClose={close}
          onEdit={onEdit}
          onRegenerate={onRegenerate}
          onUseIt={onUseIt}
        />
      ) : null}
    </>
  );
}

function defaultPostTypeFor(p) {
  const k = String(p || "").toLowerCase();
  if (k === "tiktok") return "Feed";
  if (k === "facebook") return "Post";
  if (k === "linkedin") return "Post";
  return "Feed";
}

function ExpandedSocial({ platform, postType, brand, asset, caption, hashtags, onClose, onEdit, onRegenerate, onUseIt }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${platform} ${postType} preview`}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(10,27,58,0.55)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(960px, 100%)",
          maxHeight: "calc(100vh - 48px)",
          background: "rgba(255,255,255,0.96)",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 30px 80px rgba(10,27,58,0.45)",
          padding: 28,
          display: "grid",
          gridTemplateColumns: "minmax(0, 380px) 1fr",
          gap: 28,
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 12 }}>
          <PlatformBadge platform={platform} postType={postType} />
          <div style={{ borderRadius: 18, overflow: "hidden", boxShadow: "0 18px 40px -16px rgba(10,27,58,0.35)" }}>
            <PlatformMockup
              platform={platform}
              postType={postType}
              brand={brand}
              asset={asset}
              caption={caption}
              hashtags={hashtags}
            />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h2 style={{ margin: 0, fontSize: 20, color: TEXT, fontWeight: 700 }}>Preview</h2>
            <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", color: TEXT2, fontSize: 22, cursor: "pointer" }}>×</button>
          </div>
          <Field label="Caption">
            <div style={{ fontSize: 14, lineHeight: 1.55, color: TEXT, whiteSpace: "pre-wrap" }}>{caption || "—"}</div>
          </Field>
          {hashtags && hashtags.length ? (
            <Field label="Hashtags">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {hashtags.map((h, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, color: BLUE_DEEP, background: BLUE_LIGHT, padding: "4px 10px", borderRadius: 999 }}>#{h.replace(/^#/, "")}</span>
                ))}
              </div>
            </Field>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: "auto", paddingTop: 16 }}>
            {onEdit ? <Btn onClick={onEdit}>Edit</Btn> : null}
            {onRegenerate ? <Btn onClick={onRegenerate}>Regenerate</Btn> : null}
            {onUseIt ? <Btn primary onClick={onUseIt}>Use it</Btn> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function Btn({ children, primary, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 700,
        color: primary ? WHITE : TEXT,
        background: primary ? `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)` : WHITE,
        border: primary ? "none" : `1px solid ${BORDER}`,
        cursor: "pointer",
        boxShadow: primary ? "0 8px 16px -8px rgba(14,165,233,0.55)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function PlatformBadge({ platform, postType }) {
  const meta = PLATFORM_META[platform] || PLATFORM_META.instagram;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "5px 12px 5px 6px", borderRadius: 999,
      background: BLUE_LIGHT, border: `1px solid ${BORDER}`,
    }}>
      <span aria-hidden style={{
        width: 22, height: 22, borderRadius: 6,
        background: meta.gradient,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontSize: 11, fontWeight: 800,
      }}>{meta.glyph}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: TEXT, textTransform: "capitalize" }}>{meta.name} · {postType}</span>
    </div>
  );
}

const PLATFORM_META = {
  instagram: { name: "Instagram", glyph: "Ig", gradient: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" },
  facebook:  { name: "Facebook",  glyph: "f",  gradient: "linear-gradient(135deg, #1877F2, #0a5dc2)" },
  tiktok:    { name: "TikTok",    glyph: "TT", gradient: "linear-gradient(135deg, #25F4EE, #000000 50%, #FE2C55)" },
  linkedin:  { name: "LinkedIn",  glyph: "in", gradient: "linear-gradient(135deg, #0A66C2, #084d92)" },
};

// ─── Platform mockups ────────────────────────────────────────

function PlatformMockup({ platform, postType, brand, asset, caption, hashtags, compact }) {
  if (platform === "instagram" && postType === "reel") return <IGReel brand={brand} asset={asset} caption={caption} hashtags={hashtags} compact={compact} />;
  if (platform === "instagram" && postType === "story") return <IGStory brand={brand} asset={asset} caption={caption} compact={compact} />;
  if (platform === "instagram") return <IGFeed brand={brand} asset={asset} caption={caption} hashtags={hashtags} compact={compact} />;
  if (platform === "facebook")  return <FBPost brand={brand} asset={asset} caption={caption} compact={compact} />;
  if (platform === "tiktok")    return <TikTok brand={brand} asset={asset} caption={caption} hashtags={hashtags} compact={compact} />;
  if (platform === "linkedin")  return <LinkedInPost brand={brand} asset={asset} caption={caption} compact={compact} />;
  return <IGFeed brand={brand} asset={asset} caption={caption} hashtags={hashtags} compact={compact} />;
}

function handle(brand) {
  return (brand.handle || (brand.name || "yourbrand").toLowerCase().replace(/\s/g, ""));
}
function letter(brand) { return (brand.name || "Y").trim()[0] || "Y"; }
function MediaImg({ asset, ratio = "1/1" }) {
  const placeholder = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0EA5E9"/><stop offset="1" stop-color="#0284C7"/></linearGradient></defs><rect width="600" height="600" fill="url(#g)"/><text x="300" y="310" text-anchor="middle" fill="rgba(255,255,255,0.8)" font-family="system-ui" font-size="22" font-weight="700">Your visual</text></svg>');
  const url = asset?.url || placeholder;
  const isVideo = asset?.type === "video";
  return (
    <div style={{ width: "100%", aspectRatio: ratio, background: "#0A1B3A", overflow: "hidden", position: "relative" }}>
      {isVideo ? (
        <video src={url} muted loop autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <img src={url} alt={asset?.alt || "post media"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      )}
    </div>
  );
}

function IGFeed({ brand, asset, caption, hashtags, compact }) {
  const cap = String(caption || "");
  const maxLen = compact ? 72 : 240;
  return (
    <div style={{ background: WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontSize: 13, color: "#262626" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "9px 11px", gap: 8 }}>
        <Avatar brand={brand} ring />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{handle(brand)}</div>
        </div>
        <div style={{ letterSpacing: 2, color: "#262626" }}>···</div>
      </div>
      <MediaImg asset={asset} ratio="1/1" />
      <div style={{ padding: "9px 11px" }}>
        <div style={{ display: "flex", gap: 14, marginBottom: 6 }}>
          {iconHeart()}
          {iconChat()}
          {iconSend()}
          <span style={{ marginLeft: "auto" }}>{iconBookmark()}</span>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>1,247 likes</div>
        <div style={{ fontSize: 13, lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600 }}>{handle(brand)} </span>
          {cap.slice(0, maxLen)}{cap.length > maxLen ? "…" : ""}
        </div>
        {hashtags && hashtags.length && !compact ? (
          <div style={{ fontSize: 13, color: "#00376b", marginTop: 2 }}>
            {hashtags.slice(0, 6).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function IGReel({ brand, asset, caption, hashtags, compact }) {
  return (
    <div style={{ position: "relative", aspectRatio: "9/16", background: "#000", overflow: "hidden", fontFamily: '-apple-system, system-ui, sans-serif', color: "#fff" }}>
      <MediaImg asset={asset} ratio="9/16" />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.0) 50%, rgba(0,0,0,0.55) 100%)" }} />
      <div style={{ position: "absolute", top: 12, left: 12, right: 12, display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 600 }}>
        <span>Reels</span>
        <span>{iconCamera("#fff")}</span>
      </div>
      <div style={{ position: "absolute", right: 10, bottom: 80, display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
        {sideAction(iconHeart("#fff"), "8.2k")}
        {sideAction(iconChat("#fff"), "412")}
        {sideAction(iconSend("#fff"), "Share")}
      </div>
      <div style={{ position: "absolute", left: 12, right: 60, bottom: 18, textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Avatar brand={brand} ring size={28} />
          <div style={{ fontSize: 13, fontWeight: 700 }}>{handle(brand)}</div>
          <button style={pillBtn}>Follow</button>
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.45, marginBottom: 4 }}>{(caption || "").slice(0, compact ? 60 : 140)}</div>
        {hashtags && hashtags.length && !compact ? (
          <div style={{ fontSize: 12, opacity: 0.9 }}>{hashtags.slice(0, 4).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
        ) : null}
      </div>
    </div>
  );
}

function IGStory({ brand, asset, caption, compact }) {
  return (
    <div style={{ position: "relative", aspectRatio: "9/16", background: "#000", overflow: "hidden", fontFamily: '-apple-system, system-ui, sans-serif', color: "#fff" }}>
      <MediaImg asset={asset} ratio="9/16" />
      <div style={{ position: "absolute", top: 8, left: 8, right: 8, display: "flex", gap: 3 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1, height: 2, background: "rgba(255,255,255,0.35)", borderRadius: 2 }}>
            <div style={{ width: i === 0 ? "60%" : "0%", height: "100%", background: "#fff", borderRadius: 2 }} />
          </div>
        ))}
      </div>
      <div style={{ position: "absolute", top: 18, left: 10, right: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <Avatar brand={brand} size={26} />
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>{handle(brand)}</div>
        <div style={{ fontSize: 11, opacity: 0.8 }}>· now</div>
      </div>
      <div style={{ position: "absolute", left: 18, right: 18, bottom: "30%", textAlign: "center", textShadow: "0 2px 6px rgba(0,0,0,0.5)" }}>
        <div style={{ fontWeight: 800, fontSize: compact ? 16 : 22, lineHeight: 1.25 }}>
          {(caption || "Your story copy here").slice(0, compact ? 50 : 100)}
        </div>
      </div>
      <div style={{ position: "absolute", left: 12, right: 60, bottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <input readOnly placeholder="Send message" style={{ flex: 1, padding: "8px 12px", borderRadius: 999, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", fontSize: 12, outline: "none" }} />
        {iconHeart("#fff", 18)}
        {iconSend("#fff", 18)}
      </div>
    </div>
  );
}

function FBPost({ brand, asset, caption, compact }) {
  return (
    <div style={{ background: WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', fontSize: 14, color: "#050505" }}>
      <div style={{ display: "flex", alignItems: "center", padding: 12, gap: 10 }}>
        <Avatar brand={brand} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{brand.name || "Your Page"}</div>
          <div style={{ fontSize: 12, color: "#65676b" }}>Just now · 🌍</div>
        </div>
        <div style={{ fontSize: 18, color: "#65676b" }}>···</div>
      </div>
      <div style={{ padding: "0 12px 10px", fontSize: 14, color: "#050505", lineHeight: 1.45 }}>
        {(caption || "").slice(0, compact ? 90 : 220)}
      </div>
      <MediaImg asset={asset} ratio="1.91/1" />
      <div style={{ padding: "8px 12px", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#65676b" }}>
        <span>👍❤️🔥 312</span>
        <span>42 comments · 12 shares</span>
      </div>
      <div style={{ display: "flex", borderTop: "1px solid #e4e6eb" }}>
        {["Like", "Comment", "Share"].map((l) => (
          <button key={l} style={fbBtn}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function TikTok({ brand, asset, caption, hashtags, compact }) {
  return (
    <div style={{ position: "relative", aspectRatio: "9/16", background: "#000", overflow: "hidden", fontFamily: '-apple-system, system-ui, sans-serif', color: "#fff" }}>
      <MediaImg asset={asset} ratio="9/16" />
      <div style={{ position: "absolute", top: 14, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 18, fontSize: 14, fontWeight: 600 }}>
        <span style={{ opacity: 0.6 }}>Following</span>
        <span style={{ borderBottom: "2px solid #fff", paddingBottom: 2 }}>For You</span>
      </div>
      <div style={{ position: "absolute", right: 10, bottom: 88, display: "flex", flexDirection: "column", gap: 18, alignItems: "center" }}>
        <Avatar brand={brand} size={42} />
        {sideAction(iconHeart("#fff"), "124k")}
        {sideAction(iconChat("#fff"), "2.1k")}
        {sideAction(iconBookmark("#fff"), "8.4k")}
        {sideAction(iconSend("#fff"), "Share")}
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", animation: "vellaSpin 3s linear infinite" }}>
          {iconMusic("#fff", 18)}
        </div>
      </div>
      <style>{`@keyframes vellaSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
      <div style={{ position: "absolute", left: 12, right: 70, bottom: 22, textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>@{handle(brand)}</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.45, marginBottom: 6 }}>{(caption || "").slice(0, compact ? 60 : 140)}</div>
        {hashtags && hashtags.length ? (
          <div style={{ fontSize: 12, marginBottom: 6 }}>{hashtags.slice(0, 4).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
        ) : null}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.95 }}>
          {iconMusic("#fff", 12)} Original Sound — {brand.name || handle(brand)}
        </div>
      </div>
    </div>
  );
}

function LinkedInPost({ brand, asset, caption, compact }) {
  return (
    <div style={{ background: WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', fontSize: 14, color: "#000" }}>
      <div style={{ display: "flex", alignItems: "center", padding: 12, gap: 10 }}>
        <Avatar brand={brand} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{brand.name || "Your Company"}</div>
          <div style={{ fontSize: 12, color: "#56687a" }}>4,128 followers</div>
          <div style={{ fontSize: 11, color: "#56687a" }}>Promoted · 🌐</div>
        </div>
      </div>
      <div style={{ padding: "0 12px 10px", fontSize: 14, color: "#000", lineHeight: 1.45 }}>
        {(caption || "").slice(0, compact ? 110 : 280)}
      </div>
      <MediaImg asset={asset} ratio="1.91/1" />
      <div style={{ padding: "8px 12px", fontSize: 12, color: "#56687a", display: "flex", justifyContent: "space-between" }}>
        <span>👍❤️💡 1,204</span>
        <span>148 comments · 23 reposts</span>
      </div>
      <div style={{ display: "flex", borderTop: "1px solid #e4e6eb" }}>
        {["Like", "Comment", "Repost", "Send"].map((l) => (
          <button key={l} style={{ ...fbBtn, color: "#56687a" }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────

function Avatar({ brand, size = 32, ring }) {
  const url = brand?.avatarUrl;
  const inner = (
    <div style={{
      width: ring ? size - 4 : size,
      height: ring ? size - 4 : size,
      borderRadius: "50%",
      background: url ? `center/cover no-repeat url(${url})` : "linear-gradient(135deg,#0EA5E9,#0284C7)",
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.max(10, Math.round(size * 0.42)),
      fontWeight: 800,
    }}>
      {!url ? letter(brand) : null}
    </div>
  );
  if (!ring) return inner;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      padding: 2,
      background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ background: "#fff", borderRadius: "50%", padding: 1 }}>{inner}</div>
    </div>
  );
}

function sideAction(icon, label) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      {icon}
      <span style={{ fontSize: 11, fontWeight: 700 }}>{label}</span>
    </div>
  );
}

const pillBtn = {
  marginLeft: 4, padding: "2px 10px", borderRadius: 6,
  fontSize: 11, fontWeight: 700, color: "#fff",
  background: "transparent", border: "1px solid rgba(255,255,255,0.85)",
  cursor: "pointer",
};

const fbBtn = {
  flex: 1, padding: "9px 0", background: "transparent",
  border: "none", color: "#65676b", fontWeight: 600, fontSize: 13, cursor: "pointer",
};

function iconHeart(stroke = "#262626", size = 22) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
}
function iconChat(stroke = "#262626", size = 22) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
}
function iconSend(stroke = "#262626", size = 22) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
}
function iconBookmark(stroke = "#262626", size = 22) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>;
}
function iconCamera(stroke = "#262626", size = 18) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
}
function iconMusic(stroke = "#fff", size = 14) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
}
